/* RESPONSIBLE TEAM: team-inbox */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Session from 'embercom/services/session';
import type Conversation from 'embercom/objects/inbox/conversation';
import { ConversationState } from 'embercom/objects/inbox/conversation';
import storage from 'embercom/vendor/intercom/storage';
import { postRequest } from 'embercom/lib/inbox/requests';
import type LogService from 'embercom/services/log-service';
import type Nexus from 'embercom/services/nexus';
import { type NexusEvent, NexusEventName } from 'embercom/services/nexus';
import { task } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';

export enum HandlingEventType {
  Resumed = 'resumed',
  Paused = 'paused',
}

export enum PauseReason {
  Switch = 'switch',
  Close = 'close',
  Snooze = 'snooze',
  Reassign = 'reassign',
}

export interface HandlingEvent {
  conversationId: number;
  type: HandlingEventType;
  timestamp: Date;
  reason?: PauseReason;
}

interface StoredHandlingEvent {
  conversationId: number;
  type: HandlingEventType;
  timestamp: string;
  reason?: PauseReason;
}

const STORAGE_KEY_PREFIX = 'inbox.handling_events';
const ACTIVE_CONVERSATION_KEY_PREFIX = 'inbox.active_handling_conversation';

export default class ConversationHandlingService extends Service {
  @service declare session: Session;
  @service declare logService: LogService;
  @service declare nexus: Nexus;

  @tracked private activeConversationId: number | null = null;
  @tracked pendingEvents: HandlingEvent[] = [];
  private subscribedConversationTopics: Set<string> = new Set();

  // Event handler functions (bound to this instance for proper cleanup)
  private handleThreadClosedBound!: (e: NexusEvent) => void;
  private handleThreadSnoozedBound!: (e: NexusEvent) => void;
  private handleThreadAssignedBound!: (e: NexusEvent) => void;
  private flushEventsViaSendBeaconBound!: () => void;

  get storageKey(): string {
    return `${STORAGE_KEY_PREFIX}-${this.session.workspace.id}-${this.session.teammate.id}`;
  }

  get activeConversationStorageKey(): string {
    return `${ACTIVE_CONVERSATION_KEY_PREFIX}-${this.session.workspace.id}-${this.session.teammate.id}`;
  }

  constructor() {
    super(...arguments);
    this.loadFromStorage();
    this.bindEventHandlers();
    this.subscribeToNexusEvents();
    this.subscribeToPageHide();

    // eslint-disable-next-line no-console
    console.log('[AHT] Subscribed to nexus events');
  }

  willDestroy(): void {
    super.willDestroy();
    this.unsubscribeFromNexusEvents();
    this.unsubscribeFromPageHide();
    this.unsubscribeFromConversationTopics();

    // eslint-disable-next-line no-console
    console.log('[AHT] Unsubscribed from nexus events');
  }

  private bindEventHandlers(): void {
    this.handleThreadClosedBound = this.handleThreadClosed.bind(this);
    this.handleThreadSnoozedBound = this.handleThreadSnoozed.bind(this);
    this.handleThreadAssignedBound = this.handleThreadAssigned.bind(this);
    this.flushEventsViaSendBeaconBound = this.flushEventsViaSendBeacon.bind(this);
  }

  private subscribeToNexusEvents(): void {
    this.nexus.addListener(NexusEventName.ThreadClosed, this.handleThreadClosedBound);
    this.nexus.addListener(NexusEventName.ThreadSnoozed, this.handleThreadSnoozedBound);
    this.nexus.addListener(NexusEventName.ThreadAssigned, this.handleThreadAssignedBound);
  }

  private unsubscribeFromNexusEvents(): void {
    this.nexus.removeListener(NexusEventName.ThreadClosed, this.handleThreadClosedBound);
    this.nexus.removeListener(NexusEventName.ThreadSnoozed, this.handleThreadSnoozedBound);
    this.nexus.removeListener(NexusEventName.ThreadAssigned, this.handleThreadAssignedBound);
  }

  private subscribeToPageHide(): void {
    window.addEventListener('pagehide', this.flushEventsViaSendBeaconBound);
  }

  private unsubscribeFromPageHide(): void {
    window.removeEventListener('pagehide', this.flushEventsViaSendBeaconBound);
  }

  private unsubscribeFromConversationTopics(): void {
    if (this.subscribedConversationTopics.size > 0) {
      this.nexus.unsubscribeTopics(Array.from(this.subscribedConversationTopics));
    }
  }

  private loadFromStorage(): void {
    try {
      // Load pending events (scoped by workspace and teammate)
      let eventsJson = storage.get(this.storageKey);
      if (eventsJson) {
        let storedEvents = JSON.parse(eventsJson) as StoredHandlingEvent[];
        this.pendingEvents = storedEvents.map((e) => ({
          conversationId: e.conversationId,
          type: e.type,
          timestamp: new Date(e.timestamp),
          reason: e.reason,
        }));

        // Subscribe to conversation topics for all conversations with pending events
        let conversationIds = new Set(this.pendingEvents.map((e) => e.conversationId));
        conversationIds.forEach((conversationId) => {
          let topic = `conversation/${conversationId}`;
          this.nexus.subscribeTopics([topic]);
          this.subscribedConversationTopics.add(topic);
        });
      }

      // Load active conversation (scoped by workspace and teammate)
      let activeConvId = storage.get(this.activeConversationStorageKey);
      if (activeConvId) {
        this.activeConversationId = Number(activeConvId);
        // eslint-disable-next-line no-console
        console.log('[AHT] Restored active conversation', {
          conversationId: this.activeConversationId,
          workspaceId: this.session.workspace.id,
          teammateId: this.session.teammate.id,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[AHT] Failed to load from storage', error);
      // Clear corrupted data
      this.clearStorage();
    }
  }

  private saveToStorage(): void {
    try {
      // Save pending events (scoped by workspace and teammate)
      let storedEvents: StoredHandlingEvent[] = this.pendingEvents.map((e) => ({
        conversationId: e.conversationId,
        type: e.type,
        timestamp: e.timestamp.toISOString(),
        reason: e.reason,
      }));
      storage.set(this.storageKey, JSON.stringify(storedEvents));

      // Save active conversation (scoped by workspace and teammate)
      if (this.activeConversationId) {
        storage.set(this.activeConversationStorageKey, String(this.activeConversationId));
      } else {
        storage.remove(this.activeConversationStorageKey);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[AHT] Failed to save to storage', error);
    }
  }

  private clearStorage(): void {
    storage.remove(this.storageKey);
    storage.remove(this.activeConversationStorageKey);
    this.pendingEvents = [];
  }

  private shouldTrackConversation(conversation: Conversation): boolean {
    let isOpen = conversation.state === ConversationState.Open;
    let isAssignedToMe = conversation.adminAssignee?.id === this.session.teammate.id;
    let isValidType =
      !conversation.isBackOfficeTicket &&
      !conversation.isSideConversation &&
      !conversation.isTrackerTicket;

    return isOpen && isAssignedToMe && isValidType;
  }

  private createResumeEvent(conversationId: number): void {
    let event: HandlingEvent = {
      conversationId,
      type: HandlingEventType.Resumed,
      timestamp: new Date(),
    };

    this.pendingEvents.push(event);
    this.activeConversationId = conversationId;
    this.saveToStorage();

    // eslint-disable-next-line no-console
    console.log('[AHT] Resume event created', {
      conversationId,
      timestamp: event.timestamp.toISOString(),
      adminId: this.session.teammate.id,
      pendingCount: this.pendingEvents.length,
    });
  }

  private createPauseEvent(conversationId: number, reason?: PauseReason): void {
    let event: HandlingEvent = {
      conversationId,
      type: HandlingEventType.Paused,
      timestamp: new Date(),
      reason,
    };

    this.pendingEvents.push(event);

    if (this.activeConversationId === conversationId) {
      this.activeConversationId = null;
    }

    this.saveToStorage();

    // eslint-disable-next-line no-console
    console.log('[AHT] Pause event created', {
      conversationId,
      timestamp: event.timestamp.toISOString(),
      reason: reason || 'none',
      adminId: this.session.teammate.id,
      pendingCount: this.pendingEvents.length,
    });
  }

  resumeConversation(conversation: Conversation): void {
    let isTrackable = this.shouldTrackConversation(conversation);

    // Don't create duplicate resume if already viewing this trackable conversation
    if (isTrackable && this.activeConversationId === conversation.id) {
      return;
    }

    // Pause the previous conversation if switching
    if (this.activeConversationId && this.activeConversationId !== conversation.id) {
      this.createPauseEvent(this.activeConversationId, PauseReason.Switch);
      // Keep subscription active - we still want events for conversations with pending events
    }

    // Create resume event only for trackable conversations
    if (isTrackable) {
      this.createResumeEvent(conversation.id);

      // Subscribe to this conversation's topic to receive close/snooze/reassign events
      let topic = `conversation/${conversation.id}`;
      if (!this.subscribedConversationTopics.has(topic)) {
        this.nexus.subscribeTopics([topic]);
        this.subscribedConversationTopics.add(topic);
        // eslint-disable-next-line no-console
        console.log('[AHT] Subscribed to conversation topic', conversation.id);
      }
    }
  }

  @task({ drop: true })
  *flushEventsTask(): TaskGenerator<void> {
    if (this.pendingEvents.length === 0) {
      return;
    }

    try {
      yield this.persistEvents();
      this.clearStorage();
      // eslint-disable-next-line no-console
      console.log('[AHT] Events flushed successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[AHT] Failed to flush events', error);
      this.logService.log(error);
    }
  }

  private async persistEvents(): Promise<void> {
    let apiEvents = this.pendingEvents.map((e) => ({
      conversation_id: e.conversationId,
      type: e.type,
      timestamp: e.timestamp.toISOString(),
      reason: e.reason,
    }));

    // eslint-disable-next-line no-console
    console.log('[AHT] Persisting events to API', {
      count: apiEvents.length,
      workspaceId: this.session.workspace.id,
      teammateId: this.session.teammate.id,
    });

    await postRequest(`/ember/inbox/handling_events/batch?app_id=${this.session.workspace.id}`, {
      events: apiEvents,
    });
  }

  private updateRecentPauseReason(conversationId: number, newReason: PauseReason): boolean {
    // Find the last event for this conversation
    let lastEventForConversation = this.pendingEvents
      .slice()
      .reverse()
      .find((e) => e.conversationId === conversationId);

    // Check if the last event is a pause event with 'switch' reason
    if (
      !lastEventForConversation ||
      lastEventForConversation.type !== HandlingEventType.Paused ||
      lastEventForConversation.reason !== PauseReason.Switch
    ) {
      // eslint-disable-next-line no-console
      console.log(
        '[AHT] Cannot update pause reason - last event is not a pause with switch reason',
        {
          conversationId,
          lastEventType: lastEventForConversation?.type,
          lastEventReason: lastEventForConversation?.reason,
        },
      );
      return false;
    }

    // Check if pause event was created recently (within last 2 minutes)
    let now = new Date();
    let pauseTimestamp = lastEventForConversation.timestamp;
    let timeDiff = Math.abs(now.getTime() - pauseTimestamp.getTime());

    if (timeDiff < 120000) {
      // 2 minutes (120000ms)
      lastEventForConversation.reason = newReason;
      this.saveToStorage();
      // eslint-disable-next-line no-console
      console.log('[AHT] Updated pause event reason', {
        conversationId,
        oldReason: PauseReason.Switch,
        newReason,
        timeDiff,
        pauseTimestamp: pauseTimestamp.toISOString(),
      });
      return true;
    }

    // eslint-disable-next-line no-console
    console.log('[AHT] Cannot update pause reason - time difference too large', {
      conversationId,
      timeDiff,
      threshold: 120000,
    });
    return false;
  }

  private handleConversationStateChange(
    conversationId: number,
    reason: PauseReason,
    performedByAdminId: number,
  ): void {
    let hasPendingEvents = this.pendingEvents.some((ev) => ev.conversationId === conversationId);

    if (this.activeConversationId === conversationId) {
      // Still viewing this conversation - pause it with the given reason
      this.createPauseEvent(conversationId, reason);
    } else if (performedByAdminId === this.session.teammate.id) {
      // Not actively viewing - try to update recent pause event reason
      this.updateRecentPauseReason(conversationId, reason);
    }

    // Flush events for this conversation if we have any
    if (hasPendingEvents || this.activeConversationId === conversationId) {
      taskFor(this.flushEventsTask).perform();
    }
  }

  private handleThreadClosed(e: NexusEvent): void {
    // eslint-disable-next-line no-console
    console.log('[AHT] 🔵 ThreadClosed received', e);

    let conversationId = Number(
      e.eventData.conversationId || e.eventData.conversation_id || e.eventData.id,
    );
    let performedByAdminId = Number(e.eventData.lastActivityOwnerId);

    // eslint-disable-next-line no-console
    console.log('[AHT] ThreadClosed', {
      conversationId,
      activeConversationId: this.activeConversationId,
      performedByAdminId,
    });

    this.handleConversationStateChange(conversationId, PauseReason.Close, performedByAdminId);
  }

  private handleThreadSnoozed(e: NexusEvent): void {
    // eslint-disable-next-line no-console
    console.log('[AHT] 🔵 ThreadSnoozed received', e);

    let conversationId = Number(
      e.eventData.conversationId || e.eventData.conversation_id || e.eventData.id,
    );
    let performedByAdminId = Number(e.eventData.lastActivityOwnerId);

    // eslint-disable-next-line no-console
    console.log('[AHT] ThreadSnoozed', {
      conversationId,
      activeConversationId: this.activeConversationId,
      performedByAdminId,
    });

    this.handleConversationStateChange(conversationId, PauseReason.Snooze, performedByAdminId);
  }

  private handleThreadAssigned(e: NexusEvent): void {
    // eslint-disable-next-line no-console
    console.log('[AHT] 🔵 ThreadAssigned received', e);

    let conversationId = Number(
      e.eventData.conversationId || e.eventData.conversation_id || e.eventData.id,
    );
    let assigneeIdWas = e.eventData.assigneeIdWas || e.eventData.assignee_id_was;
    let assigneeId = e.eventData.assigneeId || e.eventData.assignee_id;
    let performedByAdminId = Number(e.eventData.lastActivityOwnerId);

    // Only handle if reassigned away from current teammate
    if (assigneeIdWas !== this.session.teammate.id) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log('[AHT] ThreadAssigned', {
      conversationId,
      activeConversationId: this.activeConversationId,
      from: assigneeIdWas,
      to: assigneeId,
      performedByAdminId,
    });

    this.handleConversationStateChange(conversationId, PauseReason.Reassign, performedByAdminId);
  }

  private flushEventsViaSendBeacon(): void {
    if (this.pendingEvents.length === 0) {
      return;
    }

    let csrfToken =
      document.querySelector("meta[name='csrf-token']")?.getAttribute('content') || '';

    let payload = JSON.stringify({
      authenticity_token: csrfToken,
      events: this.pendingEvents.map((event) => ({
        conversation_id: event.conversationId,
        type: event.type,
        timestamp: event.timestamp.toISOString(),
        reason: event.reason,
      })),
    });

    let url = `/ember/inbox/handling_events/batch?app_id=${this.session.workspace.id}`;

    // eslint-disable-next-line no-console
    console.log('[AHT] Sending via sendBeacon', {
      count: this.pendingEvents.length,
      workspaceId: this.session.workspace.id,
    });

    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'conversation-handling-service': ConversationHandlingService;
  }
}
